<script lang="ts">
  import Gallery from '$components/product-card/gallery.svelte';
  import { storyblokEditable } from '$lib/actions/storyblok-editable';
  import { cn } from '$lib/utils';

  import type { CarouselSectionStoryblok } from '$types/bloks';
  import ResponsiveImage from './marketing-sub-blocks/responsive-image.svelte';
  import { getAnchorFromCmsLink } from '$lib/utils/cms';
  import ResponsiveVideo from './responsive-video.svelte';
  import SliderButton from '$components/ui/slider-button.svelte';

  export let block: CarouselSectionStoryblok;
  export let size: number | undefined = undefined;

  let containerWidth = 0;
  let screenSize = 0;
  let scroll: HTMLDivElement;
  const SCROLL_BY = 300;

  $: scrollLeftMax = scroll?.scrollWidth;
  $: scrollLeft = 0;

  $: link = block.link?.id ? $getAnchorFromCmsLink(block.link) : null;

  let next: () => void;
  let prev: () => void;
</script>

<svelte:window bind:innerWidth={screenSize} />
<section use:storyblokEditable={block} class="px-2 lg:px-0 py-8">
  <div
    class={cn('container mx-auto px-2 lg:px-8 2xl:px-0 gap-4')}
    bind:clientWidth={containerWidth}
  >
    <div class={cn('flex mb-4', block.title ? 'justify-between' : 'justify-end')}>
      {#if block.title}
        <h2
          class={cn(
            'relative text-4xl font-semibold',
            block.variant === 'center' && 'max-w-[480px] text-8xl'
          )}
        >
          {block.title}
        </h2>
      {/if}
      <div class="gap-2 px-2 lg:px-0 hidden lg:flex shrink-0 w-fit h-fit">
        <SliderButton
          icon="chevron-left"
          variant="ghost"
          disabled={scrollLeft === 0}
          class="shrink-0 w-fit"
          on:click={() => {
            scroll?.scrollBy({ left: -SCROLL_BY, behavior: 'smooth' });
          }}
        />
        <SliderButton
          icon="chevron-right"
          variant="ghost"
          class="shrink-0 w-fit"
          disabled={scrollLeftMax === scrollLeft}
          on:click={() => {
            scroll?.scrollBy({ left: SCROLL_BY, behavior: 'smooth' });
          }}
        />
      </div>
    </div>
  </div>

  {#if block.assets && block.assets.length > 0}
    <!-- Mobile -->
    <div class="container mx-auto px-2 gap-4 block lg:hidden">
      <Gallery
        let:itemClass
        bind:next
        bind:prev
        href={link?.href}
        total={(block.assets || []).length}
        class={cn('grow isolation-auto')}
      >
        {#each block.assets || [] as asset}
          <div class="min-w-full h-full overflow-hidden aspect-[373/520]">
            {#if asset.component == 'responsive-image'}
              <ResponsiveImage
                image={asset}
                imgClass={cn(itemClass)}
                sizes={{ default: '500px' }}
                widths={[
                  200 * (size || 1),
                  250 * (size || 1),
                  300 * (size || 1),
                  350 * (size || 1),
                  500 * (size || 1)
                ]}
              />
            {:else}
              <ResponsiveVideo block={asset} videoClass={cn(itemClass, 'object-cover')} />
            {/if}
          </div>
        {/each}
      </Gallery>
    </div>

    <!-- Desktop -->
    <div
      class="gap-2 overflow-auto hide-scrollbar lg:gap-4 w-full hidden lg:flex"
      style:--item-margin={`${(screenSize - containerWidth) / 2 || 16}px`}
      bind:this={scroll}
      on:scroll={() => {
        scrollLeftMax = scroll?.scrollWidth - screenSize;
        scrollLeft = scroll.scrollLeft;
      }}
    >
      {#each block.assets as asset, i}
        <div
          class={cn(
            'shrink-0 rounded-md overflow-hidden',
            i === 0 && 'ml-4 lg:ml-8 2xl:ml-[--item-margin]',
            i === block.assets.length - 1 && 'mr-4 lg:mr-8 2xl:mr-[--item-margin]'
          )}
        >
          {#if asset.component == 'responsive-image'}
            <ResponsiveImage
              image={asset}
              imgClass="h-[520px] w-auto"
              sizes={{ default: '1000px' }}
              widths={[
                200 * (size || 1),
                250 * (size || 1),
                300 * (size || 1),
                350 * (size || 1),
                400 * (size || 1),
                500 * (size || 1)
              ]}
            />
          {:else}
            <ResponsiveVideo block={asset} videoClass="object-cover h-[520px] w-auto" />
          {/if}
        </div>
      {/each}
    </div>
  {/if}
</section>
